import { ContextMenuAction } from "@iventis/components";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { Content } from "@iventis/translations";
import { translate } from "@iventis/translations/translation";
import { getRequiredDataFieldsForLayer } from "@iventis/datafields";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { sidebar } from "./state/sidebar.slice";
import { SidebarContent } from "./state/sidebar.slice.types";
import { ContextMenuActions } from "./sidebar-context-menu";
import { dispatch } from "./state/root.store";
import { DisabledCopyMessage } from "./sidebar-context-menu-components";
import { CopyActionValidationStatus } from "./sidebar-toolbar-types";

export type SidebarContextMenuActionId =
    | "VIEW_BY"
    | "VIEW_BY_OBJECT"
    | "VIEW_BY_ATTRIBUTES"
    | "SHOW_LAYER_INFORMATION"
    | "SHOW_LAYER_PROPERTIES"
    | "SHOW_ATTRIBUTE_TABLE"
    | "SHOW_PERMISSIONS"
    | "DELETE"
    | "COPY"
    | "EXPORT"
    | "IMPORT_GEOJSON"
    | "ADD_DATA_FIELDS"
    | "MOVE_LAYER_TO_BACK"
    | "MOVE_LAYER_TO_FRONT"
    | "TOGGLE_ALL_LAYERS_VISIBILITY";

/** Creates a view by map objects and view by each attribute buttons */
export const createViewByAction = (
    mapLayer: DomainLayer,
    handleViewByObjectClicked: () => void,
    handleDataFieldClicked: (dataField: DataField) => void
): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "VIEW_BY",
    displayName: translate(Content.map5.layer_overflow.layer_view.display_text),
    icon: ["far", "cube"],
    childActions: [
        createViewByObjectAction(translate(Content.map5.layer_overflow.layer_view.view_by_object), handleViewByObjectClicked),
        ...createViewByAttributesAction(translate(Content.map5.layer_overflow.layer_view.view_by_attribute), mapLayer, handleDataFieldClicked),
    ],
    testId: "context-menu-view-by",
});

/** When clicked view the layer by map objects */
const createViewByObjectAction = (displayName: string, onClick: () => void): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "VIEW_BY_OBJECT",
    displayName,
    icon: ["far", "cube"],
    onClick,
    testId: "context-menu-view-by-object",
});

/** Creates a button for each viewable attribute, when clicked view layer by the attribute clicked */
const createViewByAttributesAction = (displayName: string, mapLayer: DomainLayer, onClick: (dataField: DataField) => void): ContextMenuAction<SidebarContextMenuActionId>[] => {
    const viewableDataFields = mapLayer?.dataFields
        ? mapLayer?.dataFields.filter(
              (df) =>
                  !getRequiredDataFieldsForLayer(mapLayer.dataFields, mapLayer.styleType)
                      .map(({ id }) => id)
                      .includes(df.id)
          )
        : [];

    return [
        {
            contextMenuActionId: "VIEW_BY_ATTRIBUTES",
            displayName,
            icon: ["far", "tag"],
            disabled: viewableDataFields?.length === 0,
            childActions: viewableDataFields
                ?.filter((df) => df.type === DataFieldType.List)
                .map<ContextMenuAction<SidebarContextMenuActionId>>((datafield) => ({
                    contextMenuActionId: "VIEW_BY_ATTRIBUTES",
                    displayName: datafield.name,
                    onClick: () => onClick(datafield),
                    testId: `context-menu-view-by-attribute-${datafield.name}`,
                })),

            testId: "context-menu-view-by-attribute",
        },
    ];
};

/** When clicked show the layer information modal */
export const createShowLayerInformationAction = (disabled: boolean, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "SHOW_LAYER_INFORMATION",
    displayName: translate(Content.map4.layer_information.title),
    icon: ["far", "info-circle"],
    testId: "context-menu-layer-info",
    disabled,
    onClick: () => {
        actions.showLayerInfo();
        actions.close();
    },
});

/** When clicked show the layer edit style modal */
export const createShowLayerPropertiesAction = (disabled: boolean, nodeId: string, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "SHOW_LAYER_PROPERTIES",
    displayName: translate(Content.map5.layer_overflow.layer_properties),
    icon: ["far", "cog"],
    disabled,
    onClick: () => {
        actions.close();
        dispatch(sidebar.actions.setModalLayer({ id: nodeId, anchor: "node" }));
    },
});

/** When clicked all selected layers in the attribute table */
export const createShowAttributeTableAction = (selectedNodeIds: string[], nodeId: string, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "SHOW_ATTRIBUTE_TABLE",
    displayName: translate(Content.map5.layer_overflow.attribute_table),
    icon: ["fas", "table"],
    onClick: (e) => {
        // If node is not selected then selected it
        if (!selectedNodeIds.includes(nodeId)) {
            actions.selectNode(e);
        }
        // Go to the attribute table (it should handle having the filter set)
        dispatch(sidebar.actions.setSidebarContent(SidebarContent.DATA_TABLE));
        // Close the overflow menu
        actions.close();
    },
});

/** When clicked show the layer's permissions */
export const createShowPermissionsAction = (actions: ContextMenuActions, disabled: boolean): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "SHOW_PERMISSIONS",
    displayName: translate(Content.map5.layer_overflow.manage_permissions),
    icon: ["far", "user-lock"],
    disabled,
    testId: "context-menu-manage-permissions",
    onClick: (e) => {
        e.stopPropagation();
        actions.close();
        actions.showLayerProperties();
    },
});

/** When clicked show delete confirmation for all selected layers */
export const createDeleteAction = (disabled: boolean, colour: string, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "DELETE",
    displayName: translate(Content.map6.contextMenu.delete),
    icon: ["far", "trash"],
    disabled,
    colour,
    testId: "context-menu-delete",
    onClick: () => {
        actions.showDeleteConfirmation();
        // Close the overflow menu
        actions.close();
    },
});

/** When clicked show copy modal for all selected layers */
export const createCopyAction = (actions: ContextMenuActions, status: CopyActionValidationStatus, nodeTypes: NodeType[]): ContextMenuAction<SidebarContextMenuActionId> => {
    const layerTypesCount = nodeTypes.filter((nt) => nt === NodeType.Layer).length;
    const groupTypesCount = nodeTypes.filter((nt) => nt === NodeType.Folder).length;
    const pluraliseLayers = layerTypesCount > 1 ? "s" : "";
    const pluraliseGroups = groupTypesCount > 1 ? "s" : "";

    const copyBlurbText = () => {
        if (layerTypesCount > 0 && groupTypesCount > 0) {
            return translate(Content.map6.contextMenu.copy_layer_and_group, {
                pluraliseLayers,
                pluraliseGroups,
            });
        }
        if (layerTypesCount > 0) {
            return translate(Content.map6.contextMenu.copy_layer, {
                pluralise: pluraliseLayers,
            });
        }
        return translate(Content.map6.contextMenu.copy_group, {
            pluralise: pluraliseGroups,
        });
    };

    return {
        contextMenuActionId: "COPY",
        displayName: copyBlurbText(),
        icon: ["fas", "copy"],
        onClick: () => {
            actions.copyLayer();
            // Close the overflow menu
            actions.close();
        },
        testId: "context-menu-copy",
        disabled: status !== CopyActionValidationStatus.Valid,
        DisabledMessage: () => DisabledCopyMessage({ status }),
    };
};

export const createExportAction = (
    disableGeoJsonExports: boolean,
    disableDwgExports: boolean,
    disableKmlExports: boolean,
    actions: ContextMenuActions
): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "EXPORT",
    displayName: translate(Content.map6.contextMenu.export),
    icon: ["fal", "upload"],
    childActions: disableDwgExports
        ? [createExportGeojson(disableGeoJsonExports, actions), createExportKml(disableKmlExports, actions)] // While in devlopment, hide when not enabled in plan functionality
        : [createExportGeojson(disableGeoJsonExports, actions), createExportKml(disableKmlExports, actions), createExportDwg(disableDwgExports, actions)],
    testId: "context-menu-export",
});

/** When clicked show GeoJSON file upload modal */
export const createImportGeojsonAction = (disabled: boolean, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "IMPORT_GEOJSON",
    displayName: translate(Content.map6.contextMenu.import),
    icon: ["fal", "download"],
    disabled,
    onClick: () => {
        actions.importGeojson();
        // Close the overflow menu
        actions.close();
    },
    testId: "context-menu-import-geojson",
});

const createExportGeojson = (disabled: boolean, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "EXPORT",
    displayName: "GeoJSON (.geojson)",
    icon: ["far", "location-dot"],
    disabled,
    onClick: () => {
        actions.exportLayers("geojson");
        actions.close();
    },
    testId: "context-menu-export-geojson",
});

const createExportKml = (disabled: boolean, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "EXPORT",
    displayName: "KML (.kml)",
    icon: ["far", "earth-europe"],
    onClick: () => {
        if (disabled) {
            actions.upgradePlan.open("mapObjectKmlExport");
        } else {
            actions.exportLayers("kml");
        }
        actions.close();
    },
    testId: "context-menu-export-kml",
});

const createExportDwg = (disabled: boolean, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "EXPORT",
    displayName: "DWG (.dwg)",
    icon: ["far", "compass-drafting"],
    disabled,
    onClick: () => {
        actions.exportLayers("dwg");
        actions.close();
    },
    testId: "context-menu-export-dwg",
});

export const createAddDataFieldsToMultipleLayersAction = (actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "ADD_DATA_FIELDS",
    displayName: translate(Content.map7.contextMenu.addAttributeMultipleLayers),
    icon: ["fas", "tag"],
    onClick: () => {
        actions.addAttributes();
        actions.close();
    },
    testId: "context-menu-add-attributes",
});

export const moveLayerToBackOfMap = (actions: ContextMenuActions, disabled: boolean): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "MOVE_LAYER_TO_BACK",
    displayName: translate(Content.map8.moveLayer.back),
    icon: ["far", "send-back"],
    disabled,
    onClick: () => {
        actions.moveLayerToBackOfMap();
    },
    testId: "context-menu-move-layer-to-back",
});

export const moveLayerToFrontOfMap = (actions: ContextMenuActions, disabled: boolean): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "MOVE_LAYER_TO_FRONT",
    displayName: translate(Content.map8.moveLayer.front),
    icon: ["far", "bring-front"],
    disabled,
    onClick: () => {
        actions.moveLayerToFrontOfMap();
    },
    testId: "context-menu-move-layer-to-front",
});

/** When clicked toggle the visibility of all user layers */
export const createToggleAllLayersVisibilityAction = (allVisible: boolean, actions: ContextMenuActions): ContextMenuAction<SidebarContextMenuActionId> => ({
    contextMenuActionId: "TOGGLE_ALL_LAYERS_VISIBILITY",
    displayName: translate(allVisible ? Content.map3.sidebarButtonTooltips.hide_all_layers : Content.map3.sidebarButtonTooltips.show_all_layers),
    icon: ["far", allVisible ? "eye-slash" : "eye"],
    onClick: () => {
        actions.toggleAllLayersVisibility();
        actions.close();
    },
    testId: "context-menu-toggle-all-layers-visibility",
});
